import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, ArrowUpRight, Calendar, CheckCircle2, Clock, ExternalLink, Info, Lightbulb, Play, Star, Target, Users, Wrench } from "lucide-react";
import { projects, PROJECT_TAGS, TAG_COLORS } from "../data/mock";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const ICONS = { Star, Clock };

const META_ICON_MAP = {
	Timeline: Calendar,
	Duration: Calendar,
	Team: Users,
	"Team Size": Users,
	Role: Target,
	"My Role": Target,
	Category: Wrench,
	Discipline: Wrench,
};
const metaIcon = (label) => {
	const Icon = META_ICON_MAP[label] ?? Info;
	return <Icon className="w-4 h-4" />;
};

// ---------- media helpers ----------
const getMediaType = (item) => {
	if (item && typeof item === "object" && item.type) return item.type;
	if (typeof item === "string") {
		if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(item)) return "video";
		if (/youtube\.com|youtu\.be/.test(item)) return "youtube";
	}
	return "image";
};

const getMediaSrc = (item) => {
	if (item && typeof item === "object") return item.src;
	return item;
};

const getYouTubeEmbedId = (src) => {
	if (!src) return "";
	if (src.includes("youtu.be/")) return src.split("youtu.be/")[1].split("?")[0];
	if (src.includes("v=")) return src.split("v=")[1].split("&")[0];
	return src;
};

// Renders a single image, video file, or YouTube embed
const MediaSlide = ({ item, alt, className }) => {
	const type = getMediaType(item);
	const src = getMediaSrc(item);
	if (type === "video") {
		return (
			<video
				src={src}
				poster={item?.poster}
				className={className}
				controls
				playsInline
			/>
		);
	}
	if (type === "youtube") {
		return (
			<iframe
				src={`https://www.youtube.com/embed/${getYouTubeEmbedId(src)}`}
				className={className}
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				allowFullScreen
				title={alt}
			/>
		);
	}
	return <img src={src} alt={alt} className={className} />;
};

// Cover wrapper — handles single item or array carousel
const CoverMedia = ({ image, title }) => {
	const items = Array.isArray(image) ? image : [image];
	if (items.length === 1) {
		return (
			<div className="relative rounded-2xl overflow-hidden border border-teal-500/15 bg-[#08201d]">
				<MediaSlide item={items[0]} alt={title} className="w-full h-[320px] sm:h-[440px] lg:h-[520px] object-cover" />
				<div className="absolute inset-0 bg-gradient-to-t from-[#04100f]/40 via-transparent to-transparent pointer-events-none" />
			</div>
		);
	}
	return <MediaCarousel items={items} title={title} slideClass="h-[320px] sm:h-[440px] lg:h-[520px]" rounded="rounded-2xl" />;
};

// Carousel supporting mixed images / videos / YouTube
const MediaCarousel = ({ items, title, slideClass = "h-72 sm:h-96", rounded = "rounded-xl" }) => {
	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
	const [current, setCurrent] = useState(0);

	const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		const onSelect = () => setCurrent(emblaApi.selectedScrollSnap());
		emblaApi.on("select", onSelect);
		return () => emblaApi.off("select", onSelect);
	}, [emblaApi]);

	return (
		<div className={`relative overflow-hidden border border-teal-500/15 bg-[#08201d] ${rounded}`}>
			<div ref={emblaRef} className="overflow-hidden">
				<div className="flex">
					{items.map((item, i) => (
						<div key={i} className="flex-[0_0_100%] min-w-0">
							<MediaSlide
								item={item}
								alt={`${title} ${i + 1}`}
								className={`w-full ${slideClass} object-cover`}
							/>
						</div>
					))}
				</div>
			</div>

			<button onClick={scrollPrev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#04100f]/70 border border-teal-500/30 text-teal-300 hover:bg-[#04100f] hover:border-teal-400/60 flex items-center justify-center transition-colors">
				<ArrowLeft className="w-4 h-4" />
			</button>
			<button onClick={scrollNext} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#04100f]/70 border border-teal-500/30 text-teal-300 hover:bg-[#04100f] hover:border-teal-400/60 flex items-center justify-center transition-colors">
				<ArrowRight className="w-4 h-4" />
			</button>

			<div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
				{items.map((item, i) => {
					const type = getMediaType(item);
					return (
						<button
							key={i}
							onClick={() => emblaApi && emblaApi.scrollTo(i)}
							className={`h-1.5 rounded-full transition-all duration-300 flex items-center justify-center ${i === current ? "bg-teal-400 w-4" : "bg-slate-500 hover:bg-slate-400 w-1.5"}`}
						>
							{type !== "image" && i === current && <Play className="w-2 h-2 text-[#04100f]" />}
						</button>
					);
				})}
			</div>
		</div>
	);
};

// ---------- page ----------
const ProjectDetail = () => {
	const { slug } = useParams();
	const navigate = useNavigate();
	const idx = projects.findIndex((p) => p.slug === slug);
	const project = idx >= 0 ? projects[idx] : null;

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "instant" });
	}, [slug]);

	if (!project) {
		return (
			<div className="min-h-screen bg-[#04100f] text-slate-100">
				<Header />
				<main className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center">
					<h1 className="font-display text-4xl font-bold mb-4">Project not found</h1>
					<p className="text-slate-400 mb-8">The project you&apos;re looking for doesn&apos;t exist or may have been moved.</p>
					<Button onClick={() => navigate("/#projects")} className="bg-teal-400 text-[#04100f] hover:bg-teal-300 font-semibold">
						<ArrowLeft className="w-4 h-4 mr-2" />
						Back to projects
					</Button>
				</main>
				<Footer />
			</div>
		);
	}

	const prev = projects[(idx - 1 + projects.length) % projects.length];
	const next = projects[(idx + 1) % projects.length];

	const galleries = project.galleries || [];

	return (
		<div className="min-h-screen bg-[#04100f] text-slate-100">
			<Header />

			{/* Hero */}
			<section className="relative pt-32 pb-12 lg:pt-40 lg:pb-16 overflow-hidden">
				<div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
				<div className="absolute inset-0 bg-radial-teal pointer-events-none" />
				<div className="relative max-w-6xl mx-auto px-6 lg:px-10">
					<Link to="/#projects" className="inline-flex items-center gap-2 text-sm text-teal-300 hover:text-teal-200 mb-8 transition-colors">
						<ArrowLeft className="w-4 h-4" />
						All projects
					</Link>

					<div className="flex flex-wrap items-center gap-3 mb-5">
						<span className="text-xs font-mono uppercase tracking-[0.2em] text-teal-400">{project.category}</span>
						<span className="text-slate-600">•</span>
						<span className="text-xs font-mono text-slate-400">{project.year}</span>
						{(project.labels || []).map((key) => {
							const tag = PROJECT_TAGS[key];
							if (!tag) return null;
							const color = TAG_COLORS[tag.colorId];
							const Icon = tag.icon ? ICONS[tag.icon] : null;
							return (
								<span key={key} className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] ${color.hero}`}>
									{Icon && <Icon className="w-3 h-3" />}
									{tag.label}
								</span>
							);
						})}
					</div>

					<h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight max-w-4xl">{project.title}</h1>
					<p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">{project.summary}</p>

					<div className="mt-7 flex flex-wrap gap-2">
						{project.tags.map((t) => (
							<Badge key={t} variant="outline" className="border-teal-500/25 bg-transparent text-slate-300 text-xs font-normal">
								{t}
							</Badge>
						))}
					</div>
				</div>
			</section>

			{/* Cover */}
			<section className="relative">
				<div className="max-w-6xl mx-auto px-6 lg:px-10">
					<CoverMedia image={project.image} title={project.title} />
				</div>
			</section>

			{/* Meta strip */}
			<section className="py-12">
				<div className="max-w-6xl mx-auto px-6 lg:px-10">
					<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
						{(project.meta || []).map((m) => (
							<MetaCard key={m.label} icon={metaIcon(m.label)} label={m.label} value={m.value} />
						))}
					</div>
				</div>
			</section>

			{/* Body */}
			<section className="pb-12">
				<div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
					{/* Main column */}
					<div className="lg:col-span-8 space-y-12">
						<Block icon={<Lightbulb className="w-5 h-5" />} title="The problem">
							<p className="text-slate-300 leading-relaxed text-[17px]">{project.problem}</p>
						</Block>

						<Block icon={<Wrench className="w-5 h-5" />} title="My approach">
							<ol className="space-y-4">
								{project.approach.map((step, i) => (
									<li key={i} className="flex gap-4">
										<span className="shrink-0 w-8 h-8 rounded-lg bg-teal-500/15 border border-teal-400/30 text-teal-300 font-mono text-sm flex items-center justify-center">{String(i + 1).padStart(2, "0")}</span>
										<p className="text-slate-300 leading-relaxed pt-1">{step}</p>
									</li>
								))}
							</ol>
						</Block>

						<Block icon={<CheckCircle2 className="w-5 h-5" />} title="Results & impact">
							<ul className="space-y-3">
								{project.results.map((r, i) => (
									<li key={i} className="flex items-start gap-3">
										<CheckCircle2 className="w-5 h-5 text-teal-400 mt-0.5 shrink-0" />
										<span className="text-slate-300 leading-relaxed">{r}</span>
									</li>
								))}
							</ul>
						</Block>

						{galleries.map((g) => (
							<Block key={g.title} title={g.title}>
								{g.items.length === 1 ? (
									<div className="relative rounded-xl overflow-hidden border border-teal-500/15 bg-[#08201d]">
										<MediaSlide item={g.items[0]} alt={`${project.title} — ${g.title} 1`} className="w-full h-72 object-cover" />
									</div>
								) : (
									<MediaCarousel items={g.items} title={project.title} />
								)}
							</Block>
						))}
					</div>

					{/* Sidebar */}
					<aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24 lg:self-start">
						<div className="rounded-2xl border border-teal-500/15 bg-[#08201d]/60 p-6">
							<div className="text-xs font-mono uppercase tracking-widest text-teal-400 mb-4">Key metrics</div>
							<div className="grid grid-cols-2 gap-3">
								{project.metrics.map((m) => (
									<div key={m.label} className="rounded-lg bg-teal-500/5 border border-teal-500/15 px-3 py-3">
										<div className="text-lg font-semibold text-teal-300 font-display">{m.value}</div>
										<div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{m.label}</div>
									</div>
								))}
							</div>
						</div>

						{project.links && project.links.length > 0 && (
							<div className="rounded-2xl border border-teal-500/15 bg-[#08201d]/60 p-6">
								<div className="text-xs font-mono uppercase tracking-widest text-teal-400 mb-4">Links</div>
								<div className="space-y-2">
									{project.links.map((l) => (
										<a key={l.label} href={l.url} target="_blank" rel="noreferrer" className="flex items-center justify-between p-3 rounded-lg border border-teal-500/15 hover:border-teal-400/40 hover:bg-teal-500/5 text-slate-200 hover:text-teal-200 transition-colors">
											<span className="text-sm">{l.label}</span>
											<ExternalLink className="w-4 h-4" />
										</a>
									))}
								</div>
							</div>
						)}

						<div className="rounded-2xl border border-teal-500/20 bg-gradient-to-br from-[#08201d] to-[#04100f] p-6">
							<div className="text-sm text-slate-300 mb-4">Like what you see? Let&apos;s talk about how I can bring this kind of work to your team.</div>
							<Button asChild className="w-full bg-teal-400 text-[#04100f] hover:bg-teal-300 font-semibold">
								<Link to="/#contact">
									Get in touch
									<ArrowUpRight className="w-4 h-4 ml-2" />
								</Link>
							</Button>
						</div>
					</aside>
				</div>
			</section>

			{/* Prev / Next */}
			<section className="py-12 border-t border-teal-500/10">
				<div className="max-w-6xl mx-auto px-6 lg:px-10 grid sm:grid-cols-2 gap-4">
					<NavCard direction="prev" project={prev} />
					<NavCard direction="next" project={next} />
				</div>
			</section>

			<Footer />
		</div>
	);
};

const MetaCard = ({ icon, label, value }) => (
	<div className="rounded-xl border border-teal-500/15 bg-[#08201d]/60 p-4">
		<div className="flex items-center gap-2 text-teal-300 mb-2">
			{icon}
			<span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">{label}</span>
		</div>
		<div className="text-slate-100 text-sm font-medium">{value}</div>
	</div>
);

const Block = ({ icon, title, children }) => (
	<div>
		<div className="flex items-center gap-3 mb-5">
			{icon && <div className="w-10 h-10 rounded-lg bg-teal-500/15 text-teal-300 flex items-center justify-center">{icon}</div>}
			<h2 className="font-display text-2xl sm:text-3xl font-bold">{title}</h2>
		</div>
		<div>{children}</div>
	</div>
);

const NavCard = ({ direction, project }) => {
	const isPrev = direction === "prev";
	return (
		<Link to={`/projects/${project.slug}`} className={`group rounded-2xl border border-teal-500/15 bg-[#08201d]/60 p-6 hover:border-teal-400/40 hover:bg-[#0a2a26]/60 transition-colors flex items-center gap-4 ${isPrev ? "" : "sm:flex-row-reverse sm:text-right"}`}>
			{isPrev
				? <ArrowLeft className="w-5 h-5 text-teal-300 shrink-0 group-hover:-translate-x-1 transition-transform" />
				: <ArrowRight className="w-5 h-5 text-teal-300 shrink-0 group-hover:translate-x-1 transition-transform" />
			}
			<div className="min-w-0">
				<div className="text-xs font-mono uppercase tracking-widest text-teal-400 mb-1">{isPrev ? "Previous project" : "Next project"}</div>
				<div className="font-display text-lg font-semibold text-slate-100 group-hover:text-teal-200 transition-colors truncate">{project.title}</div>
				<div className="text-xs text-slate-400 mt-0.5">{project.category}</div>
			</div>
		</Link>
	);
};

export default ProjectDetail;
